import { AxiosResponse } from 'axios';

import axiosInstance from 'api/base';
import { normalizePageData, normalizeRedirectLink } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IPageType, IRedirectLinkResponse, TObject } from 'utils/types';

import {
  normalizeChargebackStatistics,
  normalizeEditData,
  normalizeTransaction,
  normalizeTransactionCard,
  normalizeTransactionCascading,
  normalizeTransactionCustomer,
  normalizeTransactionDetails,
  normalizeTransactionOperationHistory,
  normalizeTransactionRoutingHistory,
  normalizeTransactionWebhooks,
} from './normalizers';
import {
  IChargebackStatistics,
  IChargebackStatisticsData,
  IReconciliationForm,
  IRefundTransactionForm,
  ITransaction,
  ITransactionCard,
  ITransactionCascading,
  ITransactionCustomer,
  ITransactionDetails,
  ITransactionEditData,
  ITransactionOperationHistory,
  ITransactionRoutingHistory,
  ITransactionWebhooks,
} from './types';

const transactionAPI = {
  async getAllTransactions(
    filters: IAllPageFilters,
    onlyMain: boolean,
  ): Promise<IPageType<ITransaction>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get(`transactions`, {
      params: { ...normalizedFilter, 'only-main': onlyMain },
    });

    return normalizePageData<TObject, ITransaction>(
      response.data,
      normalizeTransaction,
      JSON.stringify(filters.search),
    );
  },

  async getTransactionDetails(id: string): Promise<ITransactionDetails> {
    const response = await axiosInstance.get(`transactions/${id}`);
    return normalizeTransactionDetails(response.data);
  },

  async getTransactionCard(id: string): Promise<ITransactionCard> {
    const response = await axiosInstance.get(`transactions/${id}/card`);
    return normalizeTransactionCard(response.data);
  },

  async getTransactionCustomer(id: string): Promise<ITransactionCustomer> {
    const response = await axiosInstance.get(`transactions/${id}/customers`);
    return normalizeTransactionCustomer(response.data);
  },

  async getTransactionOperationHistory(
    id: string,
  ): Promise<ITransactionOperationHistory> {
    const response = await axiosInstance.get(`transactions/${id}/operations`);
    return normalizeTransactionOperationHistory(response.data);
  },

  async updateTransaction(
    id: string,
    payload: ITransactionEditData,
  ): Promise<AxiosResponse> {
    const normalizedData = normalizeEditData<ITransactionEditData, ITransactionEditData>(
      payload,
    );

    const response = await axiosInstance.post(
      `transactions/${id}/operations`,
      normalizedData,
    );
    return response.data;
  },

  async refundTransaction(
    id: string,
    payload: IRefundTransactionForm,
  ): Promise<IRedirectLinkResponse> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.post(
      `transactions/${id}/actions/refund`,
      normalizedPayload,
    );

    return normalizeRedirectLink(response.data);
  },

  async approvePayoutTransaction(id: string): Promise<IRedirectLinkResponse> {
    const response = await axiosInstance.post(`payouts/${id}/execute`);

    return normalizeRedirectLink(response.data);
  },

  async declinePayoutTransaction(id: string): Promise<IRedirectLinkResponse> {
    const response = await axiosInstance.post(`payouts/${id}/cancel`);

    return normalizeRedirectLink(response.data);
  },

  async getTransactionWebhooks(id: string): Promise<ITransactionWebhooks> {
    const response = await axiosInstance.get(`transactions/${id}/webhooks`);
    return normalizeTransactionWebhooks(response.data);
  },

  async getTransactionCascading(id: string): Promise<ITransactionCascading> {
    const response = await axiosInstance.get(`transactions/${id}/cascading`);
    return normalizeTransactionCascading(response.data);
  },

  async getTransactionRoutingHistory(id: string): Promise<ITransactionRoutingHistory> {
    const response = await axiosInstance.get(`transactions/${id}/routing-history`);
    return normalizeTransactionRoutingHistory(response.data);
  },

  async getChargebackStatistics(
    params: IChargebackStatisticsData,
  ): Promise<IChargebackStatistics> {
    const response = await axiosInstance.get(`chargeback-statistics`, {
      params,
    });

    return normalizeChargebackStatistics(response?.data);
  },

  async reconciliation(payload: IReconciliationForm): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.post(
      'transactions/actions/checkstatus',
      normalizedPayload,
    );

    return response.data;
  },
};

export default transactionAPI;
